import { FC, Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import BasicPageLayout from "components/layouts/BasicPageLayout";
import { Loading } from "../Loading";
import { Notification } from "../Notification";

// MSAL
import { useIsAuthenticated } from "@azure/msal-react";
import { resetVerificationCodeLoading, setFromDashboard } from "app/features";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectWhiteLabelsState } from "app/data/selectors";
import { getPlatformMaintenances } from "domain/platform-maintenances/api/get-platform-maintenances";
import { PlatformMaintenancesResult } from "domain/platform-maintenances/entities/dtos/get-platform-maintenances.dto";

const isDateInRange = (
  date: Date,
  startDateISO: string,
  endDateISO: string
): boolean => {
  const startDate = new Date(startDateISO);
  const endDate = new Date(endDateISO);

  return date >= startDate && date <= endDate;
};

const StartActivation: FC = () => {
  const theme = useTheme();
  const params = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { whiteLabel, loading } = useAppSelector(selectWhiteLabelsState);
  const { t, i18n } = useTranslation(["translation", "activationPortal"]);
  const [isLoading, setIsLoading] = useState(true);
  const [platformMaintenancesData, setPlatformMaintenancesData] =
    useState<PlatformMaintenancesResult>();
  const [isInMaintenancePeriod, setIsInMaintenancePeriod] = useState(false);
  const [isInNotificationPeriod, setIsInNotificationPeriod] = useState(false);

  const fetchPlatformMaintenances = async () => {
    try {
      setIsLoading(true);

      const response = await getPlatformMaintenances();
      const results = response?.results;

      if (!results || results.length <= 0) {
        throw new Error("No data found");
      }
      const data = results[0];

      const today = new Date();

      const isInMaintenancePeriod = isDateInRange(
        today,
        data.startsAt,
        data.endsAt
      );
      const isInNotificationPeriod = isDateInRange(
        today,
        data.notificationStartsAt,
        data.notificationEndsAt
      );

      setPlatformMaintenancesData(data);
      setIsInMaintenancePeriod(isInMaintenancePeriod);
      setIsInNotificationPeriod(isInNotificationPeriod);
    } catch {
      setIsInMaintenancePeriod(false);
      setIsInNotificationPeriod(false);
      setPlatformMaintenancesData(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformMaintenances();
    i18n.changeLanguage("en");
  }, []);

  useEffect(() => {
    if (isAuthenticated || params.search.includes("fromDashboard")) {
      dispatch(setFromDashboard(false)); // intentionally setting it to false to remove the login flow from activation portal
    } else {
      dispatch(setFromDashboard(false));
    }
  }, [params, isAuthenticated]);

  const handleSubmit = () => {
    dispatch(resetVerificationCodeLoading());
    navigate("/activation-portal/verify-code");
  };

  if (isLoading) {
    return <Loading theme={theme} />;
  }

  // if (isInMaintenancePeriod) {
  //   return (
  //     <PlatformMaintenance
  //       title={platformMaintenancesData?.notificationTitle || ""}
  //       subtitle={platformMaintenancesData?.notificationDescription || ""}
  //     />
  //   );
  // }

  return (
    <>
      <BasicPageLayout
        title={t("titles.activationPortal", { ns: "activationPortal" })}
        subtitle={t("descriptions.activateDevice", { ns: "activationPortal" })}
        whiteLabelBgImage={whiteLabel?.backgroundImageUrl}
        loading={loading}
      >
        <Fragment>
          <Button
            fullWidth={true}
            theme={theme}
            disabled={isInMaintenancePeriod}
            onClick={handleSubmit}
          >
            {t("buttons.start", { ns: "activationPortal" })}
          </Button>
        </Fragment>
      </BasicPageLayout>
      {(isInNotificationPeriod || isInMaintenancePeriod) && (
        <Notification
          title={platformMaintenancesData?.notificationTitle || ""}
          subtitle={platformMaintenancesData?.notificationDescription || ""}
        />
      )}
    </>
  );
};

export default StartActivation;
